import styled from 'styled-components';

import { isDemo } from '@dreryk/demo-variant-frontend';
import { ControlsBar, StyledFlex } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import { VisitHistoryWithConfidentialDetails } from '~libs.shared.visit';
import { useGetCurrentPractitionerGlobalState } from '~libs.shared.account';

import { VisitDateTime } from '../common/visit/VisitDateTime';
import { VisitPractitioner } from '../common/visit/VisitPractitioner';
import { visitHistoryTranslationKeys } from '../translations';
import { VisitLocationText } from '../visit/visitLocation/VisitLocationText';
import { VisitEncounterModeText } from '../visit/visitEncounterMode/VisitEncounterModeText';
import { VisitConfidentialityText } from '../visit/visitConfidentiality/VisitConfidentialityText';

import { VisitHistoryDropdownOptions } from './dropdownOptions/VisitHistoryDropdownOptions';
import { VisitHistoryDocumentTabs } from './VisitHistoryDocumentTabs';
import { VisitHistoryInterviewItemHeading } from './VisitHistoryInterviewItemHeading';
import { VisitHistoryInterviewTabs } from './VisitHistoryInterviewTabs';
import { VisitHistoryDetailsItem } from './VisitHistoryItem';
import { VisitHistoryMedicalImagingSection } from './VisitHistoryMedicalImagingSection';
import { VisitHistoryOtherExaminationsSection } from './VisitHistoryOtherExaminationsSection';
import { VisitHistoryText } from './VisitHistoryText';

const StyledVisitHistoryInterviewSection = styled.div`
   display: flex;
   flex-direction: column;
   flex: 1;
   max-width: 800px;
   gap: 40px;
`;

StyledVisitHistoryInterviewSection.displayName = 'StyledVisitHistoryInterviewSection';

type VisitHistoryPanelProps = {
   readonly visitHistory: VisitHistoryWithConfidentialDetails;
   readonly patientId: number;
};

// eslint-disable-next-line max-lines-per-function
export const VisitHistoryPanel = (props: VisitHistoryPanelProps): JSX.Element => {
   const { confidentialDetails } = props.visitHistory;
   const currentPractitioner = useGetCurrentPractitionerGlobalState();

   const isConfidentialVisit = props.visitHistory.confidentialityLevel === 'CONFIDENTIAL';
   const isCurrentPractitionerAuthorized = currentPractitioner.confidentialAccessLevel === 'AUTHORIZED';
   const isOwnedByCurrentUser = props.visitHistory.ownedByCurrentUser;

   return (
      <StyledFlex gap={'32px'} direction={'column'} flex={'1'}>
         <ControlsBar testId={'visit'}>
            <ControlsBar.Main position={'left'}>
               <VisitDateTime
                  plannedBeginDate={props.visitHistory.appointmentBeginDate}
                  plannedEndDate={props.visitHistory.appointmentEndDate}
               />
               <VisitPractitioner practitioner={props.visitHistory.practitioner} />
               <VisitLocationText
                  name={props.visitHistory.visitLocationName}
                  address={props.visitHistory.visitLocationAddress}
               />
               {protectFeature(
                  'performingVideoConsultations',
                  <VisitEncounterModeText visitEncounterMode={props.visitHistory.encounterMode} />
               )}
               {isDemo() && props.visitHistory.confidentialityLevel !== 'CONFIDENTIAL' ? null : (
                  <VisitConfidentialityText restricted={props.visitHistory.confidentialityLevel === 'CONFIDENTIAL'} />
               )}
            </ControlsBar.Main>

            {isCurrentPractitionerAuthorized && isConfidentialVisit && !isOwnedByCurrentUser ? null : (
               <ControlsBar.Main position={'right'}>
                  <VisitHistoryDropdownOptions visitHistory={props.visitHistory} patientId={props.patientId} />
               </ControlsBar.Main>
            )}
         </ControlsBar>

         <StyledFlex flex={'1'} gap={'32px'}>
            <StyledVisitHistoryInterviewSection>
               <VisitHistoryDetailsItem
                  heading={
                     <VisitHistoryInterviewItemHeading
                        patientPregnant={confidentialDetails.pregnancyStatus === 'PREGNANT'}
                     />
                  }
               >
                  <VisitHistoryText
                     testId={'interview'}
                     content={confidentialDetails.interview}
                     noContent={visitHistoryTranslationKeys.interviewNotCompleted}
                  />
               </VisitHistoryDetailsItem>

               <VisitHistoryDetailsItem heading={visitHistoryTranslationKeys.subjectiveAssessment}>
                  <VisitHistoryText
                     testId={'subjectiveAssessment'}
                     content={confidentialDetails.physicalExamination}
                     noContent={visitHistoryTranslationKeys.subjectiveAssessmentNotCompleted}
                  />
               </VisitHistoryDetailsItem>

               {confidentialDetails.visitMedicalImagings.length > 0 ? (
                  <VisitHistoryDetailsItem heading={visitHistoryTranslationKeys.medicalImaging}>
                     <VisitHistoryMedicalImagingSection
                        testId={'medicalImaging'}
                        visitMedicalImagings={confidentialDetails.visitMedicalImagings}
                     />
                  </VisitHistoryDetailsItem>
               ) : null}

               {confidentialDetails.visitMedicalInformationList.length > 0 ? (
                  <VisitHistoryDetailsItem heading={visitHistoryTranslationKeys.otherExaminations}>
                     <VisitHistoryOtherExaminationsSection
                        testId={'otherExaminations'}
                        visitMedicalInformationList={confidentialDetails.visitMedicalInformationList}
                     />
                  </VisitHistoryDetailsItem>
               ) : null}

               <VisitHistoryDetailsItem heading={visitHistoryTranslationKeys.recommendations}>
                  <VisitHistoryText
                     testId={'recommendations'}
                     content={confidentialDetails.recommendations}
                     noContent={visitHistoryTranslationKeys.recommendationsNotCompleted}
                  />
               </VisitHistoryDetailsItem>
            </StyledVisitHistoryInterviewSection>

            <StyledFlex flex={'1'} direction={'column'} gap={'20px'}>
               <VisitHistoryInterviewTabs visitHistory={props.visitHistory} />
               <VisitHistoryDocumentTabs visitHistory={props.visitHistory} patientId={props.patientId} />
            </StyledFlex>
         </StyledFlex>
      </StyledFlex>
   );
};
